/* eslint-disable @typescript-eslint/no-explicit-any */
// services/auth/auth.ts
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import {
  ILoginData,
  ILoginResponse,
  IResendEmail,
  ISetPasswordRequest,
  ISignUpRequest,
  ISignUpResponse,
  IUser,
  IVerifyEmailRequest,
  // 👇 Add new types for user update
  IUserUpdateRequest,
  IUserUpdateResponse,
} from './types';
import TokenService from '../service.token';
import { useStore } from '@/store/store';

const updateUser = async (
  payload: IUserUpdateRequest & { userId: number },
): Promise<IUserUpdateResponse> => {
  // Replace :id in the endpoint with actual user ID
  const endpoint = api.auth.updateUser.replace('pk', payload.userId.toString());

  const formData = new FormData();

  // Append non-file fields
  if (payload.first_name !== undefined) formData.append('first_name', payload.first_name);
  if (payload.last_name !== undefined) formData.append('last_name', payload.last_name);
  if (payload.phone !== undefined) formData.append('phone', payload.phone);
  if (payload.address !== undefined) formData.append('address', payload.address);
  if (payload.gender !== undefined) formData.append('gender', payload.gender);
  if (payload.country !== undefined) formData.append('country', payload.country);
  if (payload.birthday !== undefined) formData.append('birthday', payload.birthday);
  if (payload.twofa_is_enabled !== undefined)
    formData.append('twofa_is_enabled', payload.twofa_is_enabled.toString());

  // Handle photo upload
  if (payload.photo) {
    if (payload.photo instanceof File) {
      formData.append('photo', payload.photo);
    } else {
      formData.append('photo', payload.photo);
    }
  }

  const response = await AxiosInstance.patch(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};
const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const setUser = useStore((state) => state.setUser);
  const { user } = useStore();

  return useMutation({
    mutationFn: (payload: Omit<IUserUpdateRequest, 'userId'>) =>
      updateUser({ ...payload, userId: user?.id || 0 }),
    onSuccess: (data: IUserUpdateResponse) => {
      // Update the user in the store
      setUser(data.user || data);
      // Invalidate and refetch user data
      queryClient.invalidateQueries(['user']);
      queryClient.setQueryData(['user'], data.user || data);
    },
    onError: (error: any) => {
      console.error('Update user error:', error);
    },
  });
};
// email registration
const postSignUp = async (payload: ISignUpRequest): Promise<ISignUpResponse> => {
  const response = await AxiosInstance.post(api.auth.signUp, payload);
  return response.data;
};

const usePostSignUp = () => {
  return useMutation({
    mutationFn: postSignUp,
  });
};

// email verification
// mutation function for verifying email
const initVerifyEmail = async (verifyEmailRequest: IVerifyEmailRequest) => {
  const response = await AxiosInstance.post(api.auth.verifyEmail, verifyEmailRequest);
  return response.data; // return only the data part
};

// custom hook that returns useMutation for creating user
const useVerifyEmailMutation = () => {
  return useMutation({
    mutationFn: initVerifyEmail,
  });
};

// mutation function for resending email
const initResendEmail = async (data: IResendEmail) => {
  const response = await AxiosInstance.post(api.auth.resendEmail, data);
  return response.data; // return only the data part
};

const useResendEmailMutation = () => {
  return useMutation({
    mutationFn: initResendEmail,
  });
};

// set password
const useGetLoggedInUserData = () => {
  const setUser = useStore((state) => state.setUser);
  const token = TokenService.getToken();

  return useQuery<IUser>({
    queryKey: ['user', token],
    queryFn: getLoggedInUser,
    onSuccess: (data: IUser) => {
      setUser(data);
    },
    enabled: !!token,
  });
};
let _userPromise: Promise<IUser> | null = null;

export const getLoggedInUserOnce = async (): Promise<IUser> => {
  if (_userPromise) return _userPromise;

  const promise = AxiosInstance.get<IUser>(api.auth.loggedInUser).then((r) => r.data);
  _userPromise = promise;

  // If the request fails we clear the cached promise so a retry can happen
  promise.catch(() => {
    _userPromise = null;
  });

  return promise;
};

export const useGetLoggedInUserOnce = () => {
  const token = TokenService.getToken();

  return useQuery<IUser>({
    queryKey: ['user'],
    queryFn: getLoggedInUserOnce,
    enabled: !!token,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    onSuccess: (data) => {
      const setUser = useStore.getState().setUser;
      setUser(data);
    },
  });
};
// set password
const setPassword = async (data: ISetPasswordRequest) => {
  const response = await AxiosInstance.post(api.auth.setPassword, data);
  return response.data;
};

const useSetPassword = () => {
  return useMutation({
    mutationFn: setPassword,
  });
};

export const authTokenKey = 'authToken';

const initLogin = async (loginData: ILoginData): Promise<ILoginResponse> => {
  const response = await AxiosInstance.post<ILoginResponse>(api.auth.signIn, {
    ...loginData,
    role: ['vendor', 'vendor_role'],
  });
  return response.data;
};

const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'login',
    mutationFn: (loginData: ILoginData) => initLogin(loginData),
    onSuccess: (data) => {
      TokenService.setToken(data.token);
      queryClient.setQueryData(authTokenKey, data.token.access);
      queryClient.invalidateQueries('user');
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

const getLoggedInUser = async (): Promise<IUser> => {
  const response = await AxiosInstance.get<IUser>(api.auth.loggedInUser);
  return response.data;
};

const initLogOut = async () => {
  return await AxiosInstance.post(api.auth.logout);
};
const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'logout',
    mutationFn: initLogOut,
    onSuccess: () => {
      TokenService.clearToken();
      queryClient.clear();
      queryClient.cancelQueries();
      localStorage.clear();
      sessionStorage.clear();
    },
  });
};

export {
  usePostSignUp,
  useVerifyEmailMutation,
  useResendEmailMutation,
  useLoginMutation,
  useGetLoggedInUserData,
  useSetPassword,
  useLogoutMutation,
  // 👇 Export the new hook
  useUpdateUser,
};
/* eslint-disable @typescript-eslint/no-explicit-any */
